import React, { useState, useEffect } from 'react'
import { Row, Col, Form, ListGroup, Dropdown } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { Note } from '@dataTypes/claimFeature.types'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import { formatDateTime } from '@utils/date.utils'
import { htmlToPlainTextPreview } from '@utils/htmlPlainText.util'

const ENHANCE_OPTIONS = [
  { value: 'rewrite', label: 'Rewrite', icon: 'ti ti-refresh' },
  { value: 'clarify', label: 'Clarify', icon: 'ti ti-bulb' },
  { value: 'summarize', label: 'Summarize', icon: 'ti ti-list' },
  { value: 'expand', label: 'Expand', icon: 'ti ti-arrows-maximize' },
]

interface ClaimMemoNoteProps {
  memoNotes: Note[]
  onUpdateNotes?: (notes: Note[]) => void
  readOnly?: boolean
  /** Opens the global note editor overlay (claim context). Omit when claim notes API context is unavailable. */
  onViewNote?: (note: Note) => void
}

const ClaimMemoNote: React.FC<ClaimMemoNoteProps> = ({
  memoNotes,
  onUpdateNotes,
  readOnly = false,
  onViewNote,
}) => {
  const [localNotes, setLocalNotes] = useState<Note[]>(memoNotes)
  const [newNote, setNewNote] = useState<Note>({
    noteId: `note-${Date.now()}`,
    author: 'Current User',
    content: '',
    date: new Date().toISOString(),
  })
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null)

  useEffect(() => {
    setLocalNotes(memoNotes || [])
  }, [memoNotes])

  const resetEditor = (): void => {
    setNewNote({
      noteId: `note-${Date.now()}`,
      author: 'Current User',
      content: '',
      date: new Date().toISOString(),
    })
    setEditingNoteIndex(null)
  }

  const handleSaveNote = (): void => {
    if (readOnly || !onUpdateNotes) return

    let updatedNotes = [...localNotes]

    if (editingNoteIndex !== null) {
      updatedNotes[editingNoteIndex] = {
        ...newNote,
        date: new Date().toISOString(),
      }
    } else {
      updatedNotes = [
        ...localNotes,
        {
          ...newNote,
          noteId: `note-${Date.now()}`,
          date: new Date().toISOString(),
        },
      ]
    }

    setLocalNotes(updatedNotes)
    onUpdateNotes(updatedNotes)
    resetEditor()
  }

  const handleEditNote = (index: number): void => {
    if (readOnly) return

    setNewNote(localNotes[index])
    setEditingNoteIndex(index)
  }

  const handleDeleteNote = (index: number): void => {
    if (readOnly || !onUpdateNotes) return

    const updatedNotes = localNotes.filter((_, i) => i !== index)
    setLocalNotes(updatedNotes)
    onUpdateNotes(updatedNotes)
  }

  return (
    <div className="claim-notes mt-2">
      {!readOnly && (
        <Row className="mb-2 gx-0 note-field">
          <Col md={12} className="p-0">
            <Form.Group controlId="formNotes">
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter any additional notes here"
                value={newNote.content}
                onChange={(e) =>
                  setNewNote({ ...newNote, content: e.target.value })
                }
              />
            </Form.Group>
          </Col>
          <Col
            md={12}
            className="p-1 d-flex justify-content-between align-items-center"
            style={{ background: '#EFF1F3', borderRadius: '0px 0px 8px 8px' }}
          >
            <div className="d-flex align-items-center">
              <span className="enhance-text me-1">
                <i className="mgc_pencil_2_ai_fill fs-12"></i>
              </span>
              <Button
                size="sm"
                className="button-secondary rephrase-btn me-1"
                leftIcon={<i className="ti ti-refresh"></i>}
              >
                Rewrite
              </Button>
              <Dropdown className="enhance-dd">
                <Dropdown.Toggle className="no-hover-btn">
                  <span className="button-icon button-icon-left">
                    <i className="mgc_magic_3_fill"></i>
                  </span>
                  Enhance
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {ENHANCE_OPTIONS.map(({ value, label, icon }) => (
                    <Dropdown.Item key={label} defaultValue={value}>
                      <span className={`me-1 enhance-dd-icon ${icon}`}></span>
                      {label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <Button
              size="sm"
              className="memo-note-save-btn"
              leftIcon={<i className="mgc mgc_send_fill"></i>}
              onClick={handleSaveNote}
            />
          </Col>
        </Row>
      )}

      <ListGroup className="notes-list">
        {localNotes.length === 0 ? (
          <ListGroup.Item className="text-muted">
            No notes available
          </ListGroup.Item>
        ) : (
          localNotes.map((note, index) => (
            <ListGroup.Item
              key={note.noteId}
              className="lg-custom-bdr claim-memo-note-item"
            >
              <div className="claim-memo-note-row claim-memo-note-header">
                <div className="claim-note-by">
                  <span className="claim-note-title-text">
                    {note.displayTitle ?? 'Note'}
                  </span>
                  <span className="claim-note-by-sep text-muted"> by </span>
                  <span className="claim-note-author-text">
                    {note.displayAuthor ?? note.author}
                  </span>
                </div>
                <div className="claim-note-list-date text-muted">
                  {note.date ? formatDateTime(note.date) : 'N/A'}
                </div>
              </div>
              <div className="claim-memo-note-row claim-memo-note-body">
                <div className="claim-note-list claim-note-list-preview">
                  {htmlToPlainTextPreview(note.content)}
                </div>
                <div className="note-list-right text-end claim-memo-note-list">
                  <ActionButtons
                    onView={
                      onViewNote ? (): void => onViewNote(note) : undefined
                    }
                    showView={Boolean(onViewNote)}
                    onEdit={readOnly ? undefined : () => handleEditNote(index)}
                    onDelete={
                      readOnly ? undefined : () => handleDeleteNote(index)
                    }
                    showEdit={!readOnly}
                    showDelete={!readOnly}
                    showMoreOptions={false}
                  />
                </div>
              </div>
            </ListGroup.Item>
          ))
        )}
      </ListGroup>
    </div>
  )
}

export default ClaimMemoNote
